// Token management utilities
export const tokenStorage = {
  getAccessToken: () => localStorage.getItem('qdn_access_token'),
  getRefreshToken: () => localStorage.getItem('qdn_refresh_token'),
  
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('qdn_access_token', accessToken);
    localStorage.setItem('qdn_refresh_token', refreshToken);
  },
  
  clearTokens: () => {
    localStorage.removeItem('qdn_access_token');
    localStorage.removeItem('qdn_refresh_token');
  },
  
  isTokenExpired: (token) => {
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      return payload.exp < currentTime;
    } catch {
      return true;
    }
  }
};

// Form validation utilities
export const validation = {
  validateEmail: (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return null;
  },

  validatePassword: (password) => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/(?=.*[a-zA-Z])/.test(password)) return 'Password must contain at least one letter';
    if (!/(?=.*[0-9])/.test(password)) return 'Password must contain at least one number';
    return null;
  },

  validateDisplayName: (displayName) => {
    if (!displayName) return 'Display name is required';
    if (displayName.trim().length < 2) return 'Display name must be at least 2 characters';
    if (displayName.length > 50) return 'Display name must be less than 50 characters';
    if (!/^[a-zA-Z0-9\s._-]+$/.test(displayName)) {
      return 'Display name can only contain letters, numbers, spaces, periods, underscores, and hyphens';
    }
    return null;
  },

  validateRequired: (value, fieldName) => {
    if (!value || !value.toString().trim()) {
      return `${fieldName} is required`;
    }
    return null;
  }
};

// Date formatting utilities
export const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
};

export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
};

// String utilities
export const truncateText = (text, maxLength = 150) => {
  if (!text || text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

export const slugify = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Error handling utilities
export const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.error) return error.error;
  return 'Something went wrong. Please try again.';
};

// User-friendly error messages
export const getFriendlyErrorMessage = (error) => {
  const message = getErrorMessage(error);
  
  // Handle rate limiting specially
  if (error?.status === 429 || message.toLowerCase().includes('too many')) {
    const retryAfter = error?.retryAfter;
    if (retryAfter) {
      const minutes = Math.ceil(retryAfter / 60);
      if (minutes > 1) {
        return `You're doing that too often. Please wait ${minutes} minutes before trying again.`;
      }
      return `You're doing that too often. Please wait ${retryAfter} seconds before trying again.`;
    }
    return "You're doing that too often. Please wait a moment before trying again.";
  }
  
  // Map technical errors to user-friendly messages
  const errorMap = {
    'Network response was not ok': 'Unable to connect. Please check your internet connection.',
    'Failed to fetch': 'Unable to connect. Please check your internet connection.',
    'Authentication required': 'Please sign in to continue.',
    'Session expired': 'Your session has expired. Please sign in again.',
    'Unauthorized': 'You do not have permission to perform this action.',
    'Permission denied': 'You do not have permission to perform this action.',
    'Admin access required': 'This action requires administrator privileges.',
    'Invalid credentials': 'Your email or password is incorrect.',
    'Invalid JSON': 'There was a problem with your request. Please try again.',
    'Validation error': 'Please check your input and try again.',
    '404': 'The requested item could not be found.',
    '500': 'A server error occurred. Please try again later.',
    'timeout': 'The request took too long. Please try again.',
  };
  
  for (const [key, friendlyMessage] of Object.entries(errorMap)) {
    if (message.toLowerCase().includes(key.toLowerCase())) {
      return friendlyMessage;
    }
  }
  
  return message;
};